/**
 * BetterNotes access gate — tier-based.
 *
 * BetterNotes is a paid feature (Starter / Pro / Business) whose cost profile
 * — $0.50/h recording + $0.15/h transcription on Recall.ai — makes a hard
 * paywall non-negotiable. Free users must never reach the hub; doing so even
 * for a moment means we burn minutes we cannot debit.
 *
 * Source of truth for plan tiers: `src/config/stripe.ts` SUBSCRIPTION_PLANS
 * (`starter` / `pro` / `business`). Legacy grandfathered users (2026-04-15
 * pricing transition — the 47€/127€/297€ crew) carry the same tier strings;
 * only their Price ID + monthly credit allocation differ, so the gate works
 * uniformly.
 *
 * Status semantics mirror Stripe's subscription lifecycle:
 *   active    — paid and current
 *   trialing  — trial period, treat as paid
 *   past_due  — payment failed but Stripe is still retrying; grace period
 *   (anything else — canceled, unpaid, incomplete*, paused) → no access
 *
 * Admins (profiles.role = 'admin') always pass for support/debug.
 *
 * DEV mode (`import.meta.env.DEV = true`) unconditionally passes so local
 * development doesn't require a live Stripe subscription. Production builds
 * (including Dev Netlify deploy, which is a real `vite build`) enforce the
 * real gate.
 */

/** Minimal profile shape — accepts anything with the relevant fields. */
export interface BetterNotesGateProfile {
  role?: string | null
  subscription_tier?: string | null
  subscription_status?: string | null
}

export const BETTERNOTES_PAID_TIERS = ['starter', 'pro', 'business'] as const
export const BETTERNOTES_ACTIVE_STATUSES = ['active', 'trialing', 'past_due'] as const

export type BetterNotesPaidTier = (typeof BETTERNOTES_PAID_TIERS)[number]
export type BetterNotesActiveStatus = (typeof BETTERNOTES_ACTIVE_STATUSES)[number]

/**
 * Returns true iff the profile is entitled to the BetterNotes hub.
 *
 * Passing `null`/`undefined` (anonymous or not-yet-loaded profile) returns
 * false — callers render a loader, not the hub, until the profile resolves.
 */
export function hasBetterNotesAccess(
  profile: BetterNotesGateProfile | null | undefined,
): boolean {
  if (!profile) return false
  if (profile.role === 'admin') return true
  // NOTE (2026-04-18): the old `if (import.meta.env.DEV) return true`
  // bypass was retired. It was convenient for devs but it also hid the
  // paywall + lock badge behaviour from QA in local. The gate now runs
  // identically in localhost, Dev Netlify, and Prod. To test unlocked
  // state locally, run:
  //   UPDATE profiles
  //     SET subscription_tier = 'starter', subscription_status = 'active'
  //   WHERE email = 'your@email';

  const tier = (profile.subscription_tier ?? '').toLowerCase()
  const status = (profile.subscription_status ?? '').toLowerCase()

  return (
    (BETTERNOTES_PAID_TIERS as readonly string[]).includes(tier) &&
    (BETTERNOTES_ACTIVE_STATUSES as readonly string[]).includes(status)
  )
}

/**
 * Returns true iff the profile is entitled to the Integrations hub
 * (Telegram + Slack notifications, public REST API + API keys).
 *
 * Same gating rule as BetterNotes — Mustapha 2026-04-21: integrations are
 * a paid-only feature ("ça en fait donc une feature payante"). Free users
 * see the page locked behind a Shop redirect, identical UX to BetterNotes.
 *
 * Implementation note: this is intentionally an alias of
 * `hasBetterNotesAccess` so a single Stripe lifecycle change unlocks both
 * features atomically. If the rule ever diverges (e.g. integrations free
 * but BN paid), split into a distinct check then.
 */
export function hasIntegrationsAccess(
  profile: BetterNotesGateProfile | null | undefined,
): boolean {
  return hasBetterNotesAccess(profile)
}
